import type { UseFormRegisterReturn } from 'react-hook-form';

import { DatePicker } from '@/components/ui/DatePicker';
import { FormSection } from '@/components/ui/FormSection';
import { Input } from '@/components/ui/Input';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { Select } from '@/components/ui/Select';
import { INDIAN_STATES } from '@/constants/institute';
import type { CollegeAdmissionFormValues } from '../schemas/collegeAdmission.schema';

/**
 * Everything a college records about a student.
 *
 * One definition rendered by both screens — the admission and the correction —
 * rather than a second copy on the edit page (CLAUDE.md §3). A field added here
 * appears on both, which is the whole point: a college that can enter a middle
 * name at admission and not afterwards is the bug this prevents.
 *
 * Only the fields. The heading, the buttons and what happens on submit belong
 * to the screen, because admitting somebody and correcting their record are not
 * the same act and should not read as though they were.
 */

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
];

const STATE_OPTIONS = INDIAN_STATES.map((state) => ({ value: state, label: state }));

/** The eleven fields this block owns. */
type CollegeFieldName = keyof CollegeAdmissionFormValues;

/**
 * Deliberately structural rather than `UseFormRegister<T>`.
 *
 * Both screens hand this the register of their own form, and those forms do not
 * hold the same values — the edit adds a standing. Asking only for "something
 * that registers these eleven names" lets both satisfy it honestly, instead of
 * casting one form's types into the other's at the call site.
 */
export interface CollegeStudentFieldsProps {
  register: (name: CollegeFieldName) => UseFormRegisterReturn;
  errors: Partial<Record<CollegeFieldName, { message?: string } | undefined>>;
}

export function CollegeStudentFields({ register, errors }: CollegeStudentFieldsProps) {
  return (
    <>
      <div className="card p-5">
        <FormSection
          title="Who they are"
          description="The name that goes on the register and every document."
        >
          <Input
            label="First name"
            required
            autoComplete="given-name"
            error={errors.firstName?.message}
            {...register('firstName')}
          />
          <Input
            label="Middle name (optional)"
            error={errors.middleName?.message}
            {...register('middleName')}
          />
          <Input
            label="Last name"
            required
            autoComplete="family-name"
            error={errors.lastName?.message}
            {...register('lastName')}
          />
        </FormSection>
      </div>

      <div className="card p-5">
        <FormSection
          title="How to reach them"
          description="A college writes to the student directly. Both are stored encrypted."
        >
          <Input
            label="Email"
            required
            type="email"
            autoComplete="email"
            placeholder="student@example.com"
            error={errors.email?.message}
            {...register('email')}
          />
          <PhoneInput
            label="Mobile number"
            required
            hint="Ten digits after +91."
            error={errors.mobile?.message}
            {...register('mobile')}
          />
        </FormSection>
      </div>

      <div className="card p-5">
        <FormSection
          title="Basic details"
          description="Everything here is optional except the admission date — a college often collects proof of identity later."
        >
          <DatePicker
            label="Admission date"
            required
            error={errors.admissionDate?.message}
            {...register('admissionDate')}
          />
          <DatePicker
            label="Date of birth (optional)"
            error={errors.dateOfBirth?.message}
            {...register('dateOfBirth')}
          />
          <Select
            label="Gender (optional)"
            placeholder="Prefer not to say"
            options={GENDER_OPTIONS}
            error={errors.gender?.message}
            {...register('gender')}
          />
          <Input
            label="Address (optional)"
            error={errors.addressLine1?.message}
            {...register('addressLine1')}
          />
          <Input
            label="Town or city (optional)"
            error={errors.city?.message}
            {...register('city')}
          />
          <Select
            label="State (optional)"
            placeholder="Choose a state"
            options={STATE_OPTIONS}
            error={errors.state?.message}
            {...register('state')}
          />
        </FormSection>
      </div>
    </>
  );
}
